<script setup lang="ts">
import type { Map } from "mapbox-gl";
import { getObservationBySiteGeo } from "@carnet/api/backoffice";
import type { GetObservationBySiteGeoData } from "@carnet/api/backoffice";
import type { LocationFilter } from "@/components/WebLocationSearch.vue";
import type { ObservationSpecieFilters } from "@/components/WebMapPopupObservations.vue";
import { apiBool } from "@/utils/api";
import { locationFilterKey, apiRadiusKm } from "@carnet/ui/utils/location";

const props = defineProps<{
  filter: LocationFilter;
}>();

const total = defineModel<number | undefined>("total");
const loading = defineModel<boolean>("loading");
const viewMode = defineModel<"list" | "map">("viewMode", { required: true });
loading.value = true;

const isMobile = useIsMobile();

const { bool, multi } = useUrlFilterValues();

const mineOnly = useMineFilter();
const withPhotoOnly = bool("withPhoto");
const taxonIds = multi("taxons");
const featureCodes = multi("specieFeatures");
const { requestLevelCodes, selectedLevelCodes } = useSpecieLevelFilter();

type GeoQuery = NonNullable<GetObservationBySiteGeoData["query"]>;

const geoQuery = computed<GeoQuery>((previous) => {
  const next: GeoQuery = {
    mineOnly: apiBool(mineOnly.value),
    withPhotoOnly: apiBool(withPhotoOnly.value),
    "taxons[]": taxonIds.value.length ? taxonIds.value : undefined,
    "features[]": featureCodes.value.length ? featureCodes.value : undefined,
    "specieLevels[]": requestLevelCodes.value,
    "center[latitude]": props.filter.center?.[1],
    "center[longitude]": props.filter.center?.[0],
    radius: apiRadiusKm(props.filter),
  };
  return previous && JSON.stringify(previous) === JSON.stringify(next) ? previous : next;
});

const specieFilters = computed<ObservationSpecieFilters>(() => ({
  mineOnly: apiBool(mineOnly.value),
  withPhotoOnly: apiBool(withPhotoOnly.value),
  "taxonIds[]": taxonIds.value.length ? taxonIds.value.map(Number) : undefined,
  "featureCodes[]": featureCodes.value.length ? featureCodes.value : undefined,
  "specieLevelCodes[]": requestLevelCodes.value,
}));

const GEO_CACHE_KEY = "observations-geo:default:v2";
const isDefaultQuery = computed(
  () =>
    !mineOnly.value &&
    !withPhotoOnly.value &&
    !taxonIds.value.length &&
    !featureCodes.value.length &&
    !selectedLevelCodes.value.length &&
    !props.filter.center
);
const geoCacheKey = computed(() => (isDefaultQuery.value ? GEO_CACHE_KEY : undefined));

const { data: geoData, status: geoStatus } = await getObservationBySiteGeo({
  composable: "useLazyFetch",
  key: "observation-sites-geo",
  path: { locale: appLocale() },
  query: geoQuery,
});

const { geoMarkers, cachedGeo, isShowingCache } = useGeoSource({ data: geoData, cacheKey: geoCacheKey });

watchEffect(() => {
  loading.value = geoStatus.value === "pending" && !isShowingCache.value;
});

const speciesCount = computed(() => (geoData.value ?? cachedGeo.value)?.data?.speciesCount);

watch(
  speciesCount,
  (n) => {
    total.value = n;
  },
  { immediate: true }
);

const { selectedId: selectedSiteId, onMarkerClick } = useMapSelection("siteId");

const locationKey = computed(() => locationFilterKey(props.filter));
useAppDeselectOnChange(selectedSiteId, [mineOnly, withPhotoOnly, taxonIds, featureCodes, requestLevelCodes, locationKey]);

const mapRef = useTemplateRef<{ mapboxMap: Map }>("mapRef");
useMapInitialFocus(() => mapRef.value?.mapboxMap, geoMarkers, selectedSiteId.value, { zoom: 11 });

const { focusOnLocation } = useMapLocationFocus(() => mapRef.value?.mapboxMap, toRef(props, "filter"));

function onMapInit() {
  focusOnLocation(false);
}
</script>

<template>
  <div class="relative">
    <ClientOnly>
      <WebMap
        ref="mapRef"
        :markers="geoMarkers"
        :active-feature-id="selectedSiteId"
        :controls-dimmed="isMobile && selectedSiteId != null"
        mode="hexgrid"
        class="relative flex-1 size-full lg:rounded-2xl"
        @init="onMapInit"
        @marker-click="onMarkerClick"
        :bottom-padding="isMobile ? 16 : 0"
      >
        <WebMapPopupObservations
          v-if="selectedSiteId && !isMobile"
          :site-id="selectedSiteId"
          :filters="specieFilters"
          @close="selectedSiteId = undefined"
        />
        <template v-if="isMobile" #bottom>
          <WebListMapMobileButton v-model="viewMode" inline />
        </template>
        <template v-if="isMobile" #pinned>
          <WebMapPopupObservations
            v-if="selectedSiteId"
            :site-id="selectedSiteId"
            :filters="specieFilters"
            @close="selectedSiteId = undefined"
          />
        </template>
      </WebMap>
    </ClientOnly>
  </div>
</template>
